import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Users, GraduationCap, CheckCircle } from "lucide-react";

const jobs = [
  {
    title: "Biogas Process Engineer",
    location: "Hyderabad",
    type: "Full Time",
    description:
      "Design and optimize anaerobic digestion systems, conduct feedstock analysis, and support CBG plant commissioning.",
  },
  {
    title: "Environmental Engineer",
    location: "Hyderabad",
    type: "Full Time",
    description:
      "Work on wastewater treatment systems including STP, ETP, RO and ZLD plant engineering and technical consulting.",
  },
  {
    title: "Research Associate (Biogas Lab)",
    location: "Hyderabad",
    type: "Full Time",
    description:
      "Perform BMP testing, SMA analysis and analytical monitoring for anaerobic digestion systems.",
  },
  {
    title: "Project Engineer",
    location: "Hyderabad",
    type: "Full Time",
    description:
      "Support project execution, site coordination, engineering design and commissioning of waste-to-energy plants.",
  },
];

export const Careers = () => {
  return (
    <div className="min-h-screen pt-20">

      {/* HERO */}
      <section className="bg-emerald-950 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">

          <motion.span
            className="text-amber-400 uppercase tracking-[0.3em] text-sm font-semibold"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            Join Our Team
          </motion.span>

          <motion.h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Careers at RENVITEK
          </motion.h1>

          <p className="text-white/70 text-lg max-w-3xl mx-auto leading-relaxed">
            Join a team dedicated to building sustainable energy and environmental
            solutions through engineering innovation and scientific research.
          </p>

        </div>
      </section>


      {/* WHY JOIN */}
      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white">
              Why Work With Us
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">

            <div className="bg-emerald-50 dark:bg-emerald-900/50 p-8">
              <Users className="w-10 h-10 text-emerald-600 mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-emerald-900 dark:text-white">
                Collaborative Team
              </h3>
              <p className="text-slate-600 dark:text-white/70">
                Work with experienced engineers, scientists, and innovators in
                renewable energy and waste management technologies.
              </p>
            </div>

            <div className="bg-emerald-50 dark:bg-emerald-900/50 p-8">
              <Briefcase className="w-10 h-10 text-emerald-600 mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-emerald-900 dark:text-white">
                Real Impact
              </h3>
              <p className="text-slate-600 dark:text-white/70">
                Contribute to projects that convert waste into renewable energy
                and help build a circular economy.
              </p>
            </div>

            <div className="bg-emerald-50 dark:bg-emerald-900/50 p-8">
              <GraduationCap className="w-10 h-10 text-emerald-600 mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-emerald-900 dark:text-white">
                Professional Growth
              </h3>
              <p className="text-slate-600 dark:text-white/70">
                Gain exposure to research, engineering design, field projects,
                and sustainability innovation.
              </p>
            </div>

          </div>
        </div>
      </section>


      {/* JOB OPENINGS */}
      <section className="py-12 md:py-10 bg-slate-50 dark:bg-emerald-900/30">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
          >
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white">
              Upcoming Opportunities
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">

            {jobs.map((job, index) => (
              <motion.div
                key={index}
                className="bg-white dark:bg-emerald-900 p-8 shadow-sm hover:shadow-lg transition"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
              >

                <h3 className="text-xl font-bold text-emerald-900 dark:text-white mb-2">
                  {job.title}
                </h3>

                <p className="text-sm text-amber-500 mb-3">
                  {job.location} • {job.type}
                </p>

                <p className="text-slate-600 dark:text-white/70 mb-6">
                  {job.description}
                </p>

                <Link to="/contact">
                  <button className="text-emerald-600 font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                    Apply Now
                    <ArrowRight size={18} />
                  </button>
                </Link>

              </motion.div>
            ))}

          </div>

        </div>

      </section>


      {/* CTA */}
      <section className="py-12 md:py-10 bg-amber-500">

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">

          <h2 className="text-3xl font-bold text-emerald-950 mb-4">
            Don’t See a Suitable Role?
          </h2>

          <p className="text-emerald-900 text-lg mb-8">
            Send us your resume and we will contact you when an opportunity
            matches your expertise.
          </p>

          <Link to="/contact">

            <button className="bg-emerald-900 hover:bg-emerald-800 text-white px-10 py-4 uppercase tracking-wider font-semibold inline-flex items-center gap-2">

              Submit Resume
              <ArrowRight size={20} />

            </button>

          </Link>

        </div>

      </section>

    </div>
  );
};

export default Careers;